
import { defineComponent, onMounted, ref, computed, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { getUser, sidebar } from '@coderhouse/common'
import { getCourses } from '@/api/courses.api'
import moment, { Moment, utc } from 'moment'
import CourseView from './CourseView.vue'
import { useMedia } from '@/utils/useMedia'

import { Carousel, Slide, Navigation } from 'vue3-carousel'
import 'vue3-carousel/dist/carousel.css'

export default defineComponent({
  components: {
    CourseView,
    Carousel,
    Slide,
    Navigation
  },
  setup(props, { emit }) {
    const { t } = useI18n()
    const loaded = ref<boolean>(false)
    const isDesktop = useMedia('(min-width: 1366px)')
    const isMediumDesktop = useMedia('(min-width: 1024px)')
    const title = ref<string>('')
    const option = ref<string>('')
    const options = ref()
    const user = ref()
    const courses = ref<any[]>([])
    const reloadCourses = ref(false)
    const carouselKey = ref(0)

    const filteredCourses = computed(() => {
      return courses.value.filter(course => course.status === option.value)
    })

    const setOption = (seletectOption: any) => {
      option.value = seletectOption.key
    }

    const getDays = (days: (number | string)[]) => {
      const mapDays = {
        1: 'monday',
        2: 'tuesday',
        3: 'wednesday',
        4: 'thursday',
        5: 'friday',
        6: 'saturday',
        7: 'sunday'
      }
      const stringDays = days.map(day => mapDays[day as keyof typeof mapDays]).map(day => t('home.courses.' + day))

      return stringDays.join(', ').replace(/(.*),/, `$1 ${t('home.courses.and')} `)
    }

    const setDefaultOptions = () => {
      options.value = {
        key: option.value,
        options: [
          {
            key: 'in-progress',
            label: t('home.courses.in-progress'),
            visible: false
          },
          {
            key: 'planned',
            label: t('home.courses.planned'),
            visible: false
          },
          {
            key: 'finished',
            label: t('home.courses.finished'),
            visible: false
          }
        ]
      }
    }

    const formatCourses = async () => {
      setDefaultOptions()
      const today = moment().format('YYYY-MM-DD')
      courses.value.map(course => {
        const startDate = moment(course.startDate).format('YYYY-MM-DD')
        course.camadaId = course.camada
        course.camada = `${t('home.courses.camada')} ${course.camada}`
        course.dates = t('home.courses.dates', {
          startDate: moment.utc(course.startDate).format('DD/MM/YY'),
          endDate: moment.utc(course.endDate).format('DD/MM/YY')
        })
        if (!course.endDate) {
          course.dates = course.dates.replace(' to ', '')
        }
        const days = getDays(course.days)
        course.days = days.length ? days : '----'

        course.hours = course.startHour
          ? t('home.courses.hours', {
              startHour: moment(course.startHour).format('HH:mm'),
              endHour: moment(course.endHour).format('HH:mm')
            })
          : '--:--'
        course.accessAvailableFrom = moment(course.startDate).subtract(7, 'days').format('YYYY-MM-DD')
        if (!course.nextClass) {
          course.hours = null
          course.dates = null
          course.days = null
          if (moment(course.accessAvailableFrom).utc().isAfter(today) && course.tags.includes('coderV3')) {
            course.availableFromMessage = t('home.available.from', {
              availableFrom: moment(course.accessAvailableFrom).format('DD/MM/YY')
            })
          }
        }
        if (course.percentage === 100) {
          course.status = 'finished'
          options.value.options[2].visible = true
        } else if (course.percentage === 0 && moment(startDate).isAfter(today)) {
          if (moment(course.accessAvailableFrom).utc().isAfter(today) && course.tags.includes('coderV3')) {
            course.availableFromMessage = t('home.available.from', {
              availableFrom: moment(course.accessAvailableFrom).format('DD/MM/YY')
            })
          }
          course.status = 'planned'
          options.value.options[1].visible = true
        } else {
          course.status = 'in-progress'
          options.value.options[0].visible = true
        }
      })
    }

    const formatOptions = () => {
      options.value.options = options.value.options.filter((opt: any) => opt.visible)
      if (courses.value.length) {
        options.value.options.map((opt: any) => {
          if (opt.visible && option.value === '') {
            option.value = opt.key
            options.value.key = opt.key
          }
        })
      }
    }

    onMounted(async () => {
      sidebar.subscribe(() => {
        carouselKey.value++ // Force carousel to resize
      })
      try {
        user.value = await getUser()
        if (user.value.fullName) {
          const username = user.value.fullName.split(' ')[0]
          title.value = t('home.courses.greetings', { username })
        }

        courses.value = await getCourses()
        await formatCourses()
        formatOptions()

        loaded.value = true
        emit('isLoaded', true)
      } catch (error) {
        console.error(error)
      }
    })

    const refreshCourses = async (e: any) => {
      try {
        if (e.detail?.addedCourses?.length > 0 || e.detail?.deletedCourses?.length > 0) {
          reloadCourses.value = true
          courses.value = await getCourses()
          await formatCourses()
          formatOptions()
          reloadCourses.value = false
          e.preventDefault()
        }
      } catch (error) {
        console.log(error)
      }
    }

    window.addEventListener('refresh-courses', async (e: any) => {
      refreshCourses(e)
    })

    onBeforeUnmount(() => {
      sidebar.unsubscribe()
    })

    return {
      t,
      title,
      option,
      options,
      setOption,
      courses,
      filteredCourses,
      isDesktop,
      isMediumDesktop,
      loaded,
      reloadCourses,
      carouselKey
    }
  }
})
